import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Link,
  Popover,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Grid } from '@mui/system';
import { DEFAULT_DROPBOX_ROOT } from 'dashboard409a/components/OperationsModal/dropbox.constants';
import type { DropboxFolderOption } from 'dashboard409a/components/OperationsModal/dropbox.types';
import { buildDropboxPath, getDropboxFolderStatusConfig, resolveDropboxPath, stripDropboxRootPrefix } from 'dashboard409a/components/OperationsModal/dropbox.utils';
import DropboxAuthRequired from 'dashboard409a/components/OperationsModal/DropboxAuthRequired';
import ConsultantCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/ConsultantCell';
import EngagementManagerCell from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/cells/EngagementManagerCell';
import { ConditionalLoader, Section, ValuationStatusTracker } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import { SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import formatCurrency from 'dashboard409a/utilities/formatCurrency';
import { DEFAULT_VISIBLE_FIELDS, getFinancialFields } from './ValuedInformation.constants';
import { ValuedInformationProps } from './ValuedInformation.props';
import {
  Container,
  Field,
  FinancialField,
  FinancialFieldsGrid,
  HeaderRow,
  Label,
  PersonnelRow,
  StatusBadge,
  StyledLinkSpan,
  ToggleContainer,
  UserSection,
  Value,
} from './ValuedInformation.styles';

interface OtherConsultantsFieldProps {
  consultants?: { name: string; imageSrc: string }[];
  fallbackName?: string;
  fallbackImageSrc?: string;
  isLoading?: boolean;
}

const OtherConsultantsField = ({ consultants, fallbackName, fallbackImageSrc, isLoading }: OtherConsultantsFieldProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  let list: { name: string; imageSrc: string }[];
  if (consultants && consultants.length > 0) {
    list = consultants;
  } else if (fallbackName && fallbackName !== '-') {
    list = [{ name: fallbackName, imageSrc: fallbackImageSrc || '' }];
  } else {
    list = [];
  }
  const first = list[0];
  const rest = list.slice(1);

  if (!first) {
    return (
      <UserSection>
        <Avatar data={[{ picture_url: '' }]} name="-" isLoading={isLoading} />
      </UserSection>
    );
  }

  return (
    <UserSection>
      <Avatar
        data={[{ picture_url: first.imageSrc }]}
        name={first.name}
        isLoading={isLoading}
      />
      {rest.length > 0 && (
        <>
          <Chip
            label={`+${rest.length}`}
            size="small"
            onClick={e => setAnchorEl(e.currentTarget)}
            sx={{ cursor: 'pointer', ml: 0.5 }}
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Stack spacing={1} sx={{ p: 1.5 }}>
              {rest.map(c => (
                <Avatar key={c.name} data={[{ picture_url: c.imageSrc }]} name={c.name} />
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </UserSection>
  );
};

const ValuedInformation = ({
  isFromModels,
  valuedInformation,
  isLoading,
  statusOptions,
  currentStatusId,
  onStatusChange,
  isStatusLoading = false,
  companyName,
  companyFolder,
  valuationFolder,
  dropboxRoot = DEFAULT_DROPBOX_ROOT,
  valuationFolderStatus = 'idle',
  valuationFolderMatchPaths = [],
  valuationFolderSimilarMatchesCount = 0,
  valuationFolderSuggestions = [],
  suggestedValuationFolderName,
  dropboxConnected,
  onDropboxConnect,
  onSearchValuationFolder,
  onCreateValuationFolder,
  onUpdateValuationFolder,
  undoProps,
  onEngagementManagerUpdate,
  onOtherConsultantsUpdate,
  onSyncCompanyDocs,
  isSyncCompanyDocsDisabled = false,
  isSyncCompanyDocsPending = false,
  onBrowseDocuments,
}: ValuedInformationProps) => {
  const [isExpanded, setIsExpanded] = useState(!isFromModels);

  const engagementManagerRow = useMemo((): SimplifiedAttioValuation | null => {
    if (!valuedInformation?.valuationId || !onEngagementManagerUpdate) {
      return null;
    }
    return {
      record_id: valuedInformation.valuationId,
      deal_value: null,
      company_name: null,
      status: null,
      valuation_date: null,
      valuation_fee: null,
      engagement_manager: valuedInformation.valuationEngagementManager || null,
      engagement_manager_id: valuedInformation.valuationEngagementManagerId || null,
      engagement_manager_image_src: valuedInformation.valuationEngagementManagerImageSrc || null,
      other_consultant: null,
      other_consultant_image_src: null,
      deal: null,
      deal_due: null,
      deal_stage: null,
      created_at: '',
      is_loading_additional_data: false,
    };
  }, [valuedInformation, onEngagementManagerUpdate]);

  const consultantRow = useMemo((): SimplifiedAttioValuation | null => {
    if (!valuedInformation?.valuationId || !onOtherConsultantsUpdate) {
      return null;
    }
    return {
      record_id: valuedInformation.valuationId,
      deal_value: null,
      company_name: null,
      status: null,
      valuation_date: null,
      valuation_fee: null,
      engagement_manager: null,
      engagement_manager_image_src: null,
      other_consultant: valuedInformation.otherConsultantName || null,
      other_consultant_id: valuedInformation.otherConsultantId || null,
      other_consultant_image_src: valuedInformation.otherConsultantImageSrc || null,
      deal: null,
      deal_due: null,
      deal_stage: null,
      created_at: '',
      is_loading_additional_data: false,
    };
  }, [valuedInformation, onOtherConsultantsUpdate]);
  const [isEditingValuationFolder, setIsEditingValuationFolder] = useState(false);
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [valuationFolderValue, setValuationFolderValue] = useState(valuationFolder || '');
  const [isSavingValuationFolder, setIsSavingValuationFolder] = useState(false);
  const [selectedValuationFolderPath, setSelectedValuationFolderPath] = useState<string | null>(null);

  // Update local state when valuationFolder prop changes
  useEffect(() => {
    if (valuationFolder !== undefined) {
      setValuationFolderValue(stripDropboxRootPrefix(valuationFolder || '', [dropboxRoot]));
      setSelectedValuationFolderPath(
        valuationFolder?.startsWith('/') ? valuationFolder : null
      );
    }
  }, [valuationFolder, dropboxRoot]);

  const handleBrowseDocuments = () => {
    if (!onBrowseDocuments) {
      return;
    }

    onBrowseDocuments();
  };

  const handleValuationFolderSync = async () => {
    if (!onUpdateValuationFolder) {
      return;
    }

    const folderNameOnly = valuationFolderValue.trim();

    // If the value hasn't changed, just exit edit mode
    if (folderNameOnly === (valuationFolder || '')) {
      setIsEditingValuationFolder(false);
      return;
    }

    setIsSavingValuationFolder(true);
    try {
      await onUpdateValuationFolder(folderNameOnly, selectedValuationFolderPath);
      setIsEditingValuationFolder(false);
    } catch {
      // Error is handled by the parent component
    } finally {
      setIsSavingValuationFolder(false);
    }
  };

  const handleValuationFolderKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValuationFolderSync();
    } else if (e.key === 'Escape') {
      setValuationFolderValue(stripDropboxRootPrefix(valuationFolder || '', [dropboxRoot]));
      setSelectedValuationFolderPath(valuationFolder?.startsWith('/') ? valuationFolder : null);
      setIsEditingValuationFolder(false);
    }
  };

  const handleSearchValuationFolderClick = async () => {
    if (!onSearchValuationFolder) {
      return;
    }
    await onSearchValuationFolder(valuationFolderValue.trim());
  };

  const handleCreateValuationFolderClick = async () => {
    if (!onCreateValuationFolder) {
      return;
    }
    await onCreateValuationFolder(valuationFolderValue.trim());
  };

  const getValuationFolderPath = (folderValue: string) => {
    const companyPath = resolveDropboxPath(companyFolder || '', dropboxRoot);
    if (companyPath && folderValue) {
      return buildDropboxPath(companyPath, folderValue);
    }
    if (folderValue) {
      return `${dropboxRoot}/${folderValue}`;
    }
    if (companyFolder) {
      return `${dropboxRoot}/${companyFolder}/`;
    }
    return `${dropboxRoot}/`;
  };

  // Display full path: {dropboxRoot}/{companyFolder}/{valuationFolder}
  let displayValuationFolderPath = '';
  const resolvedCompanyFolderPath = resolveDropboxPath(companyFolder || '', dropboxRoot);
  if (resolvedCompanyFolderPath && valuationFolder) {
    const resolvedValuationFolderPath = valuationFolder.startsWith('/')
      ? valuationFolder
      : `${resolvedCompanyFolderPath}/${valuationFolder}`;
    displayValuationFolderPath = stripDropboxRootPrefix(resolvedValuationFolderPath, [dropboxRoot]);
  } else if (valuationFolder) {
    displayValuationFolderPath = stripDropboxRootPrefix(resolveDropboxPath(valuationFolder, dropboxRoot), [dropboxRoot]);
  }

  const editingOptions = valuationFolderSuggestions.filter(option => Boolean(option.pathDisplay?.trim()));

  const isValuationActionsDisabled = !companyFolder?.trim();
  const syncButtonTooltip = useMemo(() => {
    if (!onSyncCompanyDocs) {
      return '';
    }
    if (isSyncCompanyDocsPending) {
      return 'Syncing Company Docs…';
    }
    if (isSyncCompanyDocsDisabled) {
      return 'Connect a valuation Dropbox folder before syncing documents.';
    }
    return '';
  }, [onSyncCompanyDocs, isSyncCompanyDocsPending, isSyncCompanyDocsDisabled]);

  const statusConfig = getDropboxFolderStatusConfig(valuationFolderStatus);
  let valuationStatusLabel = statusConfig.label;
  if (valuationFolderStatus === 'searching') {
    valuationStatusLabel = 'Listing folders...';
  } else if (valuationFolderStatus === 'matches') {
    valuationStatusLabel = 'Folders Loaded';
  } else if (valuationFolderStatus === 'not_found') {
    valuationStatusLabel = 'No Folders Found';
  }

  const allFields = getFinancialFields(valuedInformation);

  // Filter fields based on toggle state
  const visibleFields = allFields.filter(
    field => isExpanded || DEFAULT_VISIBLE_FIELDS.includes(field.key as (typeof DEFAULT_VISIBLE_FIELDS)[number])
  );

  let valuationFolderContent: ReactNode;
  if (isEditingValuationFolder && !isAuthRequired) {
    valuationFolderContent = (
      <Box sx={{ mt: 0.5, width: '100%' }}>
        <Stack spacing={1}>
          <TextField
            label="Company Folder (inherited)"
            value={resolveDropboxPath(companyFolder || '', dropboxRoot) || `${dropboxRoot}/`}
            size="small"
            fullWidth
            disabled
          />
          <Autocomplete<DropboxFolderOption, false, false, true>
            fullWidth
            freeSolo
            options={editingOptions}
            getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
            isOptionEqualToValue={(option, value) =>
              typeof value !== 'string'
              && option.pathDisplay === value.pathDisplay
            }
            value={valuationFolderValue}
            onChange={(_, value) => {
              if (typeof value === 'string') {
                setValuationFolderValue(value);
                setSelectedValuationFolderPath(null);
                return;
              }
              if (!value) {
                setValuationFolderValue('');
                setSelectedValuationFolderPath(null);
                return;
              }
              setValuationFolderValue(value.name);
              setSelectedValuationFolderPath(value.pathDisplay);
            }}
            onInputChange={(_, value, reason) => {
              setValuationFolderValue(value);
              if (reason === 'input' || reason === 'clear') {
                setSelectedValuationFolderPath(null);
              }
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Valuation Folder"
                placeholder={suggestedValuationFolderName || 'Enter valuation folder'}
                onKeyDown={handleValuationFolderKeyDown}
                disabled={isSavingValuationFolder || isValuationActionsDisabled}
                size="small"
                fullWidth
                autoFocus
                helperText={
                  isValuationActionsDisabled
                    ? 'Set and sync Company Folder first.'
                    : `Path: ${getValuationFolderPath(valuationFolderValue || '<folder>')}`
                }
              />
            )}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              onClick={handleSearchValuationFolderClick}
              disabled={!valuationFolderValue.trim() || isValuationActionsDisabled || valuationFolderStatus === 'searching'}
            >
              List Folders
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCreateValuationFolderClick}
              disabled={!valuationFolderValue.trim() || isValuationActionsDisabled || valuationFolderStatus === 'creating'}
            >
              Create
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleValuationFolderSync}
              disabled={
                !valuationFolderValue.trim()
                || isValuationActionsDisabled
                || isSavingValuationFolder
                || valuationFolderStatus === 'syncing'
              }
            >
              Sync
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setValuationFolderValue(stripDropboxRootPrefix(valuationFolder || '', [dropboxRoot]));
                setSelectedValuationFolderPath(valuationFolder?.startsWith('/') ? valuationFolder : null);
                setIsEditingValuationFolder(false);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  } else if (isAuthRequired && onDropboxConnect) {
    valuationFolderContent = (
      <DropboxAuthRequired
        onConnect={onDropboxConnect}
        onCancel={() => setIsAuthRequired(false)}
      />
    );
  } else {
    valuationFolderContent = (
      <Typography
        onClick={() => {
          if (!onUpdateValuationFolder) {
            return;
          }
          if (dropboxConnected === false) {
            setIsAuthRequired(true);
            return;
          }
          setValuationFolderValue(stripDropboxRootPrefix(valuationFolder || '', [dropboxRoot]) || suggestedValuationFolderName || '');
          setSelectedValuationFolderPath(valuationFolder || null);
          setIsEditingValuationFolder(true);
        }}
        sx={{
          mt: 0.5,
          width: 'fit-content',
          cursor: onUpdateValuationFolder ? 'pointer' : 'default',
          color: onUpdateValuationFolder ? 'primary.main' : 'text.primary',
          fontStyle: 'italic',
          '&:hover': onUpdateValuationFolder ? { textDecoration: 'underline' } : {},
        }}>
        {displayValuationFolderPath || 'Click here to set the folder path'}
      </Typography>
    );
  }

  return (
    <Section title="Valuation">
      <Container>
        <Grid container spacing={2} sx={{ height: '100%' }}>
          {/* Left Column - Main Content */}
          <Grid size={{ md: 8, sm: 12 }}>
            <HeaderRow>
              <Field>
                <ConditionalLoader isLoading={isLoading && !valuedInformation?.valuationName} width={150}>
                  <Link href={valuedInformation?.valuationUrl} target="_blank" rel="noopener noreferrer">
                    <StyledLinkSpan>{valuedInformation?.valuationName || '-'}</StyledLinkSpan>
                  </Link>
                </ConditionalLoader>
              </Field>
              <ConditionalLoader isLoading={isLoading && !valuedInformation?.bookingType} width={150}>
                <StatusBadge>{valuedInformation?.bookingType || '-'}</StatusBadge>
              </ConditionalLoader>
            </HeaderRow>
            {valuedInformation?.valuationNotice && <span>NOTICE: {valuedInformation.valuationNotice}</span>}
            <Box mt={0.5} />
            <ToggleContainer>
              <FormControlLabel
                control={<Switch checked={isExpanded} onChange={e => setIsExpanded(e.target.checked)} size="small" />}
                label={isExpanded ? 'Show Less' : 'Show More'}
              />
            </ToggleContainer>
            <FinancialFieldsGrid>
              {visibleFields.map(field => {
                const value = field.getValue();
                let displayValue: string = '-';
                if (field.isCurrency) {
                  if (value !== undefined && value !== null && value !== '-') {
                    displayValue = formatCurrency({
                      value: typeof value === 'number' ? value : Number(value) || 0,
                    });
                  }
                } else {
                  displayValue = (value as string) || '-';
                }
                return (
                  <FinancialField key={field.key}>
                    <Label>{field.label}</Label>
                    <ConditionalLoader isLoading={isLoading && !valuedInformation} width={150}>
                      <Value>{displayValue}</Value>
                    </ConditionalLoader>
                  </FinancialField>
                );
              })}
            </FinancialFieldsGrid>
            <PersonnelRow>
              <Field>
                <Label>Valuation Lead</Label>
                <UserSection>
                  <Avatar
                    data={[{ picture_url: valuedInformation?.valuationLeadImageSrc || '' }]}
                    name={valuedInformation?.valuationLeadName || '-'}
                    isLoading={isLoading && !valuedInformation?.valuationLeadName}
                  />
                </UserSection>
              </Field>
              <Field>
                <Label>Engagement Manager</Label>
                {engagementManagerRow ? (
                  <UserSection>
                    <EngagementManagerCell
                      valuationRow={engagementManagerRow}
                      onEngagementManagerUpdate={onEngagementManagerUpdate}
                    />
                  </UserSection>
                ) : (
                  <UserSection>
                    <Avatar
                      data={[{ picture_url: valuedInformation?.valuationEngagementManagerImageSrc || '' }]}
                      name={valuedInformation?.valuationEngagementManager || '-'}
                      isLoading={isLoading && !valuedInformation?.valuationEngagementManager}
                    />
                  </UserSection>
                )}
              </Field>
              <Field>
                <Label>Other Consultant</Label>
                {consultantRow ? (
                  <UserSection>
                    <ConsultantCell
                      valuationRow={consultantRow}
                      onOtherConsultantsUpdate={onOtherConsultantsUpdate}
                    />
                  </UserSection>
                ) : (
                  <OtherConsultantsField
                    consultants={valuedInformation?.otherConsultants}
                    fallbackName={valuedInformation?.otherConsultantName}
                    fallbackImageSrc={valuedInformation?.otherConsultantImageSrc}
                    isLoading={isLoading && !valuedInformation?.otherConsultantName}
                  />
                )}
              </Field>
            </PersonnelRow>
            <Field sx={{ mt: 2 }}>
              <Label>Company</Label>
              <Typography sx={{ mt: 0.5 }}>{companyName || '-'}</Typography>
            </Field>
            <Field sx={{ mt: 2, width: 'fit-content'}}>
              <Label>Dropbox: Valuation Folder</Label>
              {isValuationActionsDisabled && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Select and sync a Company Folder for this company before listing or syncing the valuation folder.
                </Alert>
              )}
              <ConditionalLoader isLoading={isLoading} width={200}>
                {valuationFolderContent}
              </ConditionalLoader>
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" color={statusConfig.color} label={valuationStatusLabel} />
              </Box>
              {onSyncCompanyDocs && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Tooltip title={syncButtonTooltip}>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={onSyncCompanyDocs}
                        disabled={isSyncCompanyDocsDisabled}
                      >
                        {isSyncCompanyDocsPending ? 'Syncing…' : 'Sync Company Docs'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleBrowseDocuments}
                    disabled={isSyncCompanyDocsDisabled}
                  >
                    Browse Documents
                  </Button>
                </Box>
              )}
              {valuationFolderMatchPaths.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Found at:
                  </Typography>
                  {valuationFolderMatchPaths.slice(0, 3).map(path => (
                    <Typography key={path} variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
                      {path}
                    </Typography>
                  ))}
                  {valuationFolderMatchPaths.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{valuationFolderMatchPaths.length - 3} more
                    </Typography>
                  )}
                </Box>
              )}
              {valuationFolderSimilarMatchesCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {valuationFolderSimilarMatchesCount} {valuationFolderSimilarMatchesCount === 1 ? 'folder' : 'folders'} found in the company folder. Select the correct folder from the dropdown list.
                </Typography>
              )}
            </Field>
          </Grid>

          {/* Right Column - Status Tracker */}
          {statusOptions && statusOptions.length > 0 && (
            <Grid size={{ md: 4, sm: 12 }} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                  Status
                </Typography>
              </Box>
              <Box mt={1}>
                <ValuationStatusTracker
                  statusOptions={statusOptions}
                  currentStatusId={currentStatusId}
                  onStatusChange={onStatusChange}
                  isLoading={isStatusLoading}
                  isExpanded={isExpanded}
                  undoProps={undoProps}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Section>
  );
};

export default ValuedInformation;
